import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useShallow } from 'zustand/shallow';

import { useGuardianProfileQuery } from '@/features/guardian/queries';
import {
  INITIAL_ONBOARDING_PROFILE_FORM,
  onboardingProfileSchema,
  type OnboardingProfileForm,
} from '@/features/onboarding/schemas/onboarding-profile-schema';
import { mapGuardianProfileToOnboardingForm } from '@/features/onboarding/utils/map-onboarding-profile';
import { useSessionStore } from '@/stores/session-store';

/**
 * Creates the react-hook-form instance for onboarding step 1 (profile).
 * Lives at the screen so the wizard's "Continue" button can validate the form
 * before advancing; the step component reads it back via `useFormContext`.
 *
 * Every field is prefilled from the signed-in guardian's saved profile
 * (`GET /api/v1/guardians/me`) — name, relationship, phones and address — with
 * `email` falling back to the auth user's. Prefill keeps any fields the user has
 * already edited (`keepDirtyValues`).
 */
export function useOnboardingProfileForm() {
  const user = useSessionStore(useShallow(s => s.user));
  const { data: guardian } = useGuardianProfileQuery(user?.uid);

  const form = useForm<OnboardingProfileForm>({
    mode: 'onSubmit',
    resolver: yupResolver(onboardingProfileSchema) as never,
    defaultValues: INITIAL_ONBOARDING_PROFILE_FORM,
  });

  const { reset } = form;

  const fallbackEmail = user?.email ?? '';

  useEffect(() => {
    reset(mapGuardianProfileToOnboardingForm(guardian, fallbackEmail), {
      keepDirtyValues: true,
    });
  }, [reset, guardian, fallbackEmail]);

  return form;
}

export default useOnboardingProfileForm;
